/**
 * Test play screen (W2-02; DESIGN.md §4.9, ARCHITECTURE.md §7.5, U§5 B9): the preview canvas full-window with Phira's
 * state machine (testPlayState.ts) and, in judged play, the input of judge.ts:
 *  - any letter key or the left mouse button is a touch; Space is the flick key and ONLY flicks (it is not a touch:
 *    it never judges a Tap or Hold head and does not keep holds or catch drags, so a flick next to a tap does not
 *    consume the tap); moving the mouse while a touch is held flicks;
 *  - Esc (or the pause button over the HUD's pause icon) opens the pause menu: Resume, Restart, Back to editor here,
 *    Back to where I started, autoplay / judged, rate;
 *  - F5 restarts from the same start, Shift+F5 from the chart start;
 *  - at the end: the results (judged) or a short finished panel (autoplay).
 * Keys are handled here first (`data-keys="native"`); Ctrl / Alt chords still reach the app's bindings.
 */

import { useEffect, useRef, useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { NoteId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import { useT } from '../../i18n/useT';
import { Button } from '../../ui/Button';
import { Segmented } from '../../ui/Segmented';
import { Select } from '../../ui/Select';
import { useEditorValue, useTestPlay } from './hooks';
import { JudgeResults } from './JudgeResults';
import { registerServices } from './live';
import { m } from './preview.messages';
import { previewStageOf } from './previewLoop';
import { testPlayOf, type TestPlayController } from './testPlayState';
import { useFocusWhileMounted } from './Theater';
import s from './Preview.module.css';

/** Help text shows this long after the play starts (ms). */
const HELP_MS = 5000;
const RATES: readonly number[] = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

/** Leaves test play at the first of `notes` and selects them in the editor (histogram bars). */
export function jumpToNotes(services: AppServices, tp: TestPlayController, notes: readonly NoteId[]): void {
  const { store, engine } = services;
  const first = notes[0];
  tp.exit('here');
  if (store === null || first === undefined) return;
  const n = store.doc.notes.get(first);
  if (n === undefined) return;
  const editor = services.editor.getState();
  editor.setActiveLine(n.line);
  editor.select({ notes: new Set(notes), primary: { kind: 'note', id: first } });
  if (engine !== null) services.audio.transport.seek(engine.noteSec(first));
  services.timeline?.reveal(Beats.toF64(n.t), 'playhead');
}

function isLetter(code: string): boolean {
  return /^Key[A-Z]$/.test(code);
}

export function TestPlay(props: { readonly services: AppServices }) {
  const { services } = props;
  const t = useT();
  const tp = testPlayOf(services);
  const snap = useTestPlay(services);
  const rate = useEditorValue(services, (st) => st.rate);
  const rootRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const held = useRef(new Set<string>());
  const [helpShown, setHelpShown] = useState(true);

  useEffect(() => {
    registerServices(services);
    const host = hostRef.current;
    if (host === null) return undefined;
    return previewStageOf(services).attach(host, 'testPlay');
  }, [services]);

  useFocusWhileMounted(rootRef);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHelpShown(false);
    }, HELP_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [snap.startSec, snap.mode]);

  // Losing the window releases every touch (a key released elsewhere never sends its keyup here).
  useEffect(() => {
    const keys = held.current;
    const onBlur = (): void => {
      for (const k of keys) tp.release(k);
      keys.clear();
    };
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('blur', onBlur);
    };
  }, [tp]);

  const judged = snap.mode === 'judged';
  const playing = snap.phase === 'intro' || snap.phase === 'playing';

  const press = (code: string): void => {
    if (held.current.has(code)) return;
    held.current.add(code);
    tp.press(code);
  };
  const release = (code: string): void => {
    if (!held.current.delete(code)) return;
    tp.release(code);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    // Plain keys belong to the play (and its menus): the editor's bindings never see them (Ctrl / Alt chords do).
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (playing) tp.pause();
      else if (snap.phase === 'paused') tp.resume();
      return;
    }
    if (e.code === 'F5') {
      e.preventDefault();
      e.stopPropagation();
      if (e.shiftKey) void tp.start('start');
      else tp.restart();
      return;
    }
    // Menus and results own their keys.
    if (!playing || e.target !== rootRef.current) return;
    if (judged && isLetter(e.code)) {
      e.preventDefault();
      if (!e.repeat) press(e.code);
      return;
    }
    if (e.code === 'Space') {
      e.preventDefault();
      if (e.repeat) return;
      // Judged: the dedicated flick key (not a touch, see the module comment). Autoplay: pause.
      if (judged) tp.flick();
      else tp.pause();
    }
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.code === 'Space' && judged && playing) {
      e.preventDefault();
      return;
    }
    if (isLetter(e.code)) {
      if (held.current.has(e.code)) e.preventDefault();
      release(e.code);
    }
  };

  const exitTo = (where: 'here' | 'start'): void => {
    for (const k of held.current) tp.release(k);
    held.current.clear();
    tp.exit(where);
  };

  const workspaceOpen = services.registry.command('app.workspace') !== undefined;
  const calibrateOpen = services.registry.command('timing.calibrate') !== undefined;

  return (
    <div
      ref={rootRef}
      className={s.fullWindow}
      role="application"
      aria-label={t(m.testPlayLabel)}
      aria-describedby="preview-testplay-help"
      tabIndex={-1}
      data-keys="native"
      data-test-play={snap.phase}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
    >
      <div
        ref={hostRef}
        className={s.fullStage}
        onPointerDown={(e) => {
          if (e.button !== 0 || !judged || !playing) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          rootRef.current?.focus({ preventScroll: true });
          press('mouse');
        }}
        onPointerUp={(e) => {
          if (e.button !== 0) return;
          release('mouse');
        }}
        onPointerCancel={() => {
          release('mouse');
        }}
        onPointerMove={(e) => {
          tp.pointerMove(e.clientX, e.clientY, e.timeStamp);
        }}
      />
      {playing && (
        <button
          type="button"
          className={s.pauseHotspot}
          aria-label={t(m.pauseButton)}
          title={t(m.pauseButton)}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          onClick={() => {
            tp.pause();
          }}
        />
      )}
      <div
        id="preview-testplay-help"
        className={s.fullHint}
        data-hidden={helpShown && playing ? 'false' : 'true'}
        aria-hidden={!(helpShown && playing)}
      >
        {judged ? t(m.judgedHelp) : t(m.autoplayHelp)}
      </div>
      <div className={s.srOnly} aria-live="polite" aria-atomic="true">
        {snap.lastEvent === null ? '' : t(m.gradeAnnounce, { grade: snap.lastEvent.grade })}
      </div>
      {snap.phase === 'paused' && (
        <div className={s.scrim}>
          <section className={s.card} aria-labelledby="preview-pause-title" role="dialog" data-pause-menu="">
            <h2 id="preview-pause-title" className={s.cardTitle}>
              {t(m.pauseTitle)}
            </h2>
            <div className={s.cardRow}>
              <Button
                variant="primary"
                icon="Play"
                autoFocus
                onClick={() => {
                  tp.resume();
                  rootRef.current?.focus({ preventScroll: true });
                }}
              >
                {t(m.resume)}
              </Button>
              <Button
                icon="RotateCcw"
                onClick={() => {
                  tp.restart();
                  rootRef.current?.focus({ preventScroll: true });
                }}
              >
                {t(m.restart)}
              </Button>
              <Button
                icon="MapPin"
                onClick={() => {
                  exitTo('here');
                }}
              >
                {t(m.backHere)}
              </Button>
              <Button
                icon="Undo2"
                onClick={() => {
                  exitTo('start');
                }}
              >
                {t(m.backStart)}
              </Button>
            </div>
            <div className={s.cardRow}>
              <span className={s.cardLabel}>{t(m.modeTitle)}</span>
              <Segmented<'autoplay' | 'judged'>
                ariaLabel={t(m.modeTitle)}
                value={snap.mode}
                options={[
                  { value: 'autoplay', label: t(m.modeAutoplay) },
                  { value: 'judged', label: t(m.modeJudged) },
                ]}
                onChange={(v) => {
                  tp.setMode(v);
                }}
              />
              <Select<string>
                label={t(m.rate)}
                value={String(rate)}
                options={RATES.map((r) => ({ value: String(r), label: t(m.rateOption, { rate: r.toFixed(2) }) }))}
                onChange={(v) => {
                  const r = Number(v);
                  services.editor.getState().setRate(r);
                  services.audio.transport.setRate(r);
                }}
              />
            </div>
          </section>
        </div>
      )}
      {snap.phase === 'finished' && (
        <div className={s.scrim}>
          {snap.results !== null ? (
            <JudgeResults
              results={snap.results}
              onRestart={() => {
                tp.restart();
                rootRef.current?.focus({ preventScroll: true });
              }}
              onBackHere={() => {
                exitTo('here');
              }}
              onBackStart={() => {
                exitTo('start');
              }}
              onJump={(notes) => {
                jumpToNotes(services, tp, notes);
              }}
              onOpenTiming={
                workspaceOpen
                  ? () => {
                      exitTo('start');
                      void services.registry.run('app.workspace', { workspace: 'timing' }, 'button');
                    }
                  : null
              }
              onCalibrate={
                calibrateOpen
                  ? () => {
                      exitTo('start');
                      void services.registry.run('timing.calibrate', undefined, 'button');
                    }
                  : null
              }
            />
          ) : (
            <section className={s.card} aria-labelledby="preview-finished-title" data-finished="">
              <h2 id="preview-finished-title" className={s.cardTitle}>
                {t(m.finishedTitle)}
              </h2>
              <p>{t(m.finishedAutoplay)}</p>
              <div className={s.cardRow}>
                <Button
                  variant="primary"
                  icon="RotateCcw"
                  autoFocus
                  onClick={() => {
                    tp.restart();
                    rootRef.current?.focus({ preventScroll: true });
                  }}
                >
                  {t(m.restart)}
                </Button>
                <Button
                  icon="MapPin"
                  onClick={() => {
                    exitTo('here');
                  }}
                >
                  {t(m.backHere)}
                </Button>
                <Button
                  icon="Undo2"
                  onClick={() => {
                    exitTo('start');
                  }}
                >
                  {t(m.backStart)}
                </Button>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
